import { useEffect, useState } from 'react'
import { Globe, Mail, MapPin, Instagram, Twitter, Facebook } from 'lucide-react'
import { siteApi, type SiteSettings } from '@/lib/api'

const DEFAULT_NAV_LINKS = [
  { label: 'About Me',   href: '#about' },
  { label: 'Portfolio',  href: '#portfolio' },
  { label: 'Website',    href: '#website' },
  { label: 'Reviews',    href: '#reviews' },
  { label: 'Contact Me', href: '#contact' },
]

// Brand icons tidak tersedia di lucide-react — pakai SVG langsung
function TikTokIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.27 6.27 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V8.89a8.2 8.2 0 0 0 4.79 1.52V7.04a4.85 4.85 0 0 1-1.02-.35z" />
    </svg>
  )
}

function SpotifyIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z" />
    </svg>
  )
}

function WhatsAppIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z" />
    </svg>
  )
}

// Full-screen SVG — decorative BnW elements only on left and right wings.
// Center band (x:500–940 in the 1440-wide viewBox) left clear for bg_image_url.
// SVG masks fade each wing smoothly toward center.
function AbstractSides() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 1440 900"
      preserveAspectRatio="xMidYMid slice"
      className="absolute inset-0 h-full w-full"
      aria-hidden="true"
    >
      <style>{`
        @keyframes twinkle {
          0%, 100% { opacity: 0.62; }
          45%, 55%  { opacity: 0.04; }
        }
        @keyframes twinkle-sm {
          0%, 100% { opacity: 0.40; }
          45%, 55%  { opacity: 0.03; }
        }
        @keyframes breathe {
          0%, 100% { opacity: 0.45; }
          50%       { opacity: 0.14; }
        }
        @keyframes breathe-dim {
          0%, 100% { opacity: 0.26; }
          50%       { opacity: 0.07; }
        }
        @keyframes dot-pulse {
          0%, 100% { opacity: 0.34; }
          50%       { opacity: 0.08; }
        }
      `}</style>

      <defs>
        <path id="spark" d="M0-1 L.22-.22 L1 0 L.22.22 L0 1 L-.22.22 L-1 0 L-.22-.22Z" />

        <linearGradient id="fadeL" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%"   stopColor="white" stopOpacity="1" />
          <stop offset="100%" stopColor="white" stopOpacity="0" />
        </linearGradient>
        <mask id="maskL">
          <rect x="0" y="0" width="540" height="900" fill="url(#fadeL)" />
        </mask>

        <linearGradient id="fadeR" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%"   stopColor="white" stopOpacity="0" />
          <stop offset="100%" stopColor="white" stopOpacity="1" />
        </linearGradient>
        <mask id="maskR">
          <rect x="900" y="0" width="540" height="900" fill="url(#fadeR)" />
        </mask>
      </defs>

      {/* ── LEFT WING ── */}
      <g mask="url(#maskL)">
        {/* Swoosh top-left */}
        <path d="M-20 110 Q130 190 400 270" stroke="#FFF" strokeWidth="11" fill="none" strokeLinecap="round" style={{ animation: 'breathe     8s  ease-in-out infinite' }} />
        <path d="M-10 143 Q140 218 390 295" stroke="#FFF" strokeWidth="7"  fill="none" strokeLinecap="round" style={{ animation: 'breathe-dim 10s ease-in-out infinite 2.0s' }} />
        <path d="M5   172 Q148 244 380 318" stroke="#FFF" strokeWidth="4"  fill="none" strokeLinecap="round" style={{ animation: 'breathe-dim 12s ease-in-out infinite 1.0s' }} />
        {/* Swoosh mid-left */}
        <path d="M-20 490 Q160 540 420 600" stroke="#FFF" strokeWidth="11" fill="none" strokeLinecap="round" style={{ animation: 'breathe     9s  ease-in-out infinite 3.0s' }} />
        <path d="M10  524 Q168 568 405 626" stroke="#FFF" strokeWidth="6"  fill="none" strokeLinecap="round" style={{ animation: 'breathe-dim 11s ease-in-out infinite 1.5s' }} />

        {/* Sparkles — large */}
        <g fill="#FFF">
          <use href="#spark" transform="translate(55  75)  scale(14)" style={{ animation: 'twinkle 3.1s ease-in-out infinite' }} />
          <use href="#spark" transform="translate(200 210) scale(11)" style={{ animation: 'twinkle 4.3s ease-in-out infinite 1.2s' }} />
          <use href="#spark" transform="translate(75  390) scale(9)"  style={{ animation: 'twinkle 2.7s ease-in-out infinite 0.5s' }} />
          <use href="#spark" transform="translate(255 445) scale(12)" style={{ animation: 'twinkle 3.9s ease-in-out infinite 2.0s' }} />
          <use href="#spark" transform="translate(110 630) scale(10)" style={{ animation: 'twinkle 5.2s ease-in-out infinite 0.3s' }} />
          <use href="#spark" transform="translate(300 698) scale(8)"  style={{ animation: 'twinkle 3.6s ease-in-out infinite 1.7s' }} />
          <use href="#spark" transform="translate(45  752) scale(11)" style={{ animation: 'twinkle 4.8s ease-in-out infinite 0.9s' }} />
          <use href="#spark" transform="translate(195 818) scale(7)"  style={{ animation: 'twinkle 2.5s ease-in-out infinite 2.6s' }} />
        </g>

        {/* Sparkles — small */}
        <g fill="#FFF">
          <use href="#spark" transform="translate(138 158) scale(5)"   style={{ animation: 'twinkle-sm 2.2s ease-in-out infinite 0.4s' }} />
          <use href="#spark" transform="translate(318 308) scale(4)"   style={{ animation: 'twinkle-sm 3.7s ease-in-out infinite 1.5s' }} />
          <use href="#spark" transform="translate(28  458) scale(4.5)" style={{ animation: 'twinkle-sm 4.9s ease-in-out infinite 0.8s' }} />
          <use href="#spark" transform="translate(228 548) scale(3.5)" style={{ animation: 'twinkle-sm 2.8s ease-in-out infinite 2.3s' }} />
          <use href="#spark" transform="translate(375 658) scale(4)"   style={{ animation: 'twinkle-sm 3.3s ease-in-out infinite 1.0s' }} />
        </g>

        {/* Dot cluster trails */}
        <g fill="#FFF">
          <circle cx="98"  cy="275" r="3"   style={{ animation: 'dot-pulse 4.0s ease-in-out infinite' }} />
          <circle cx="148" cy="290" r="2"   style={{ animation: 'dot-pulse 5.3s ease-in-out infinite 0.7s' }} />
          <circle cx="198" cy="304" r="2.5" style={{ animation: 'dot-pulse 6.1s ease-in-out infinite 1.4s' }} />
          <circle cx="258" cy="488" r="2"   style={{ animation: 'dot-pulse 4.7s ease-in-out infinite 0.3s' }} />
          <circle cx="308" cy="508" r="1.5" style={{ animation: 'dot-pulse 5.8s ease-in-out infinite 1.1s' }} />
          <circle cx="340" cy="528" r="2"   style={{ animation: 'dot-pulse 7.0s ease-in-out infinite 1.8s' }} />
        </g>
      </g>

      {/* ── RIGHT WING (delays offset +0.7–1.5s from left to desync) ── */}
      <g mask="url(#maskR)">
        {/* Swoosh top-right */}
        <path d="M1460 110 Q1310 190 1040 270" stroke="#FFF" strokeWidth="11" fill="none" strokeLinecap="round" style={{ animation: 'breathe     8s  ease-in-out infinite 1.5s' }} />
        <path d="M1450 143 Q1300 218 1050 295" stroke="#FFF" strokeWidth="7"  fill="none" strokeLinecap="round" style={{ animation: 'breathe-dim 10s ease-in-out infinite 3.5s' }} />
        <path d="M1435 172 Q1292 244 1060 318" stroke="#FFF" strokeWidth="4"  fill="none" strokeLinecap="round" style={{ animation: 'breathe-dim 12s ease-in-out infinite 2.5s' }} />
        {/* Swoosh mid-right */}
        <path d="M1460 490 Q1280 540 1020 600" stroke="#FFF" strokeWidth="11" fill="none" strokeLinecap="round" style={{ animation: 'breathe     9s  ease-in-out infinite 4.5s' }} />
        <path d="M1430 524 Q1272 568 1035 626" stroke="#FFF" strokeWidth="6"  fill="none" strokeLinecap="round" style={{ animation: 'breathe-dim 11s ease-in-out infinite 3.0s' }} />

        {/* Sparkles — large */}
        <g fill="#FFF">
          <use href="#spark" transform="translate(1385 75)  scale(14)" style={{ animation: 'twinkle 3.1s ease-in-out infinite 0.7s' }} />
          <use href="#spark" transform="translate(1240 210) scale(11)" style={{ animation: 'twinkle 4.3s ease-in-out infinite 1.9s' }} />
          <use href="#spark" transform="translate(1365 390) scale(9)"  style={{ animation: 'twinkle 2.7s ease-in-out infinite 1.2s' }} />
          <use href="#spark" transform="translate(1185 445) scale(12)" style={{ animation: 'twinkle 3.9s ease-in-out infinite 2.7s' }} />
          <use href="#spark" transform="translate(1330 630) scale(10)" style={{ animation: 'twinkle 5.2s ease-in-out infinite 1.0s' }} />
          <use href="#spark" transform="translate(1140 698) scale(8)"  style={{ animation: 'twinkle 3.6s ease-in-out infinite 2.4s' }} />
          <use href="#spark" transform="translate(1395 752) scale(11)" style={{ animation: 'twinkle 4.8s ease-in-out infinite 1.6s' }} />
          <use href="#spark" transform="translate(1245 818) scale(7)"  style={{ animation: 'twinkle 2.5s ease-in-out infinite 3.3s' }} />
        </g>

        {/* Sparkles — small */}
        <g fill="#FFF">
          <use href="#spark" transform="translate(1302 158) scale(5)"   style={{ animation: 'twinkle-sm 2.2s ease-in-out infinite 1.0s' }} />
          <use href="#spark" transform="translate(1122 308) scale(4)"   style={{ animation: 'twinkle-sm 3.7s ease-in-out infinite 2.1s' }} />
          <use href="#spark" transform="translate(1412 458) scale(4.5)" style={{ animation: 'twinkle-sm 4.9s ease-in-out infinite 1.4s' }} />
          <use href="#spark" transform="translate(1212 548) scale(3.5)" style={{ animation: 'twinkle-sm 2.8s ease-in-out infinite 2.9s' }} />
          <use href="#spark" transform="translate(1065 658) scale(4)"   style={{ animation: 'twinkle-sm 3.3s ease-in-out infinite 1.6s' }} />
        </g>

        {/* Dot cluster trails */}
        <g fill="#FFF">
          <circle cx="1342" cy="275" r="3"   style={{ animation: 'dot-pulse 4.0s ease-in-out infinite 0.9s' }} />
          <circle cx="1292" cy="290" r="2"   style={{ animation: 'dot-pulse 5.3s ease-in-out infinite 1.6s' }} />
          <circle cx="1242" cy="304" r="2.5" style={{ animation: 'dot-pulse 6.1s ease-in-out infinite 2.3s' }} />
          <circle cx="1182" cy="488" r="2"   style={{ animation: 'dot-pulse 4.7s ease-in-out infinite 1.2s' }} />
          <circle cx="1132" cy="508" r="1.5" style={{ animation: 'dot-pulse 5.8s ease-in-out infinite 2.0s' }} />
          <circle cx="1100" cy="528" r="2"   style={{ animation: 'dot-pulse 7.0s ease-in-out infinite 2.7s' }} />
        </g>
      </g>
    </svg>
  )
}

function buildSocialLinks(s: SiteSettings) {
  return [
    s.about_instagram  && { icon: Instagram,    label: 'Instagram',        href: `https://instagram.com/${s.about_instagram}` },
    s.about_twitter    && { icon: Twitter,       label: 'Twitter / X',      href: `https://x.com/${s.about_twitter}` },
    s.about_tiktok     && { icon: TikTokIcon,    label: 'TikTok',           href: `https://tiktok.com/@${s.about_tiktok}` },
    s.about_facebook   && { icon: Facebook,      label: 'Facebook',         href: `https://facebook.com/${s.about_facebook}` },
    s.about_spotify    && { icon: SpotifyIcon,   label: 'Spotify',          href: `https://open.spotify.com/user/${s.about_spotify}` },
    s.about_wa_channel && { icon: WhatsAppIcon,  label: 'WhatsApp Channel', href: `https://whatsapp.com/channel/${s.about_wa_channel}` },
    s.about_website_url && { icon: Globe,        label: 'Website',          href: s.about_website_url },
    s.about_email      && { icon: Mail,          label: 'Email',            href: `mailto:${s.about_email}` },
    s.about_location   && { icon: MapPin,        label: s.about_location,   href: '#' },
  ].filter(Boolean) as { icon: React.ElementType; label: string; href: string }[]
}

export default function HomePage() {
  const [settings, setSettings] = useState<SiteSettings | null>(null)

  useEffect(() => {
    siteApi.getSettings().then(setSettings).catch(() => {})
  }, [])

  const socialLinks = settings ? buildSocialLinks(settings) : []
  const navLinks = settings?.nav_links?.length ? settings.nav_links : DEFAULT_NAV_LINKS

  return (
    <>
    <style>{`
      @keyframes fadeSlideUp {
        from { opacity: 0; transform: translateY(24px); }
        to   { opacity: 1; transform: translateY(0); }
      }
      @keyframes fadeIn {
        from { opacity: 0; }
        to   { opacity: 1; }
      }
    `}</style>
    <div className="relative flex min-h-screen flex-col items-center justify-between overflow-hidden px-6 py-12 text-white">
      {/* Dark base + BnW abstract side wings */}
      <div className="absolute inset-0 bg-[#0C0C0C]">
        <div style={{ animation: 'fadeIn 1.2s ease-out 0.1s both' }} className="absolute inset-0">
          <AbstractSides />
        </div>
      </div>

      {/* Center column — bg_image_url only, no side overflow */}
      <div className="absolute inset-0 flex justify-center pointer-events-none" style={{ animation: 'fadeIn 1.0s ease-out 0.4s both' }}>
        <div className="relative h-full w-full max-w-md">
          {settings?.bg_image_url && (
            <img
              src={settings.bg_image_url}
              alt=""
              aria-hidden="true"
              className="h-full w-full object-cover object-center opacity-60"
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black/60" />
        </div>
      </div>

      {/* Nama + Title */}
      <div className="flex flex-col items-center gap-2 text-center z-10" style={{ animation: 'fadeSlideUp 0.7s ease-out 0.4s both' }}>
        <h1
          className="text-5xl text-white drop-shadow-md"
          style={{ fontFamily: '"Dancing Script", cursive', fontWeight: 700 }}
        >
          {settings?.about_name ?? 'Oktavia Dwi Anggraeni'}
        </h1>
        <p className="mt-1 text-xs font-semibold tracking-[0.25em] text-white/80 uppercase">
          {settings?.about_position ?? 'Social Media Specialist'}
        </p>
      </div>

      {/* Tombol navigasi */}
      {navLinks.length > 0 && (
        <nav className="flex w-full max-w-xs flex-col gap-3" aria-label="Navigasi utama">
          {navLinks.map((link, i) => (
            <a
              key={link.label}
              href={link.href}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center rounded-full border border-white/20 bg-white/10 px-6 py-3 text-sm font-semibold tracking-widest uppercase text-white backdrop-blur-md transition-all duration-200 hover:bg-white/20 hover:scale-[1.02] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50"
              style={{ animation: `fadeSlideUp 0.5s ease-out ${0.6 + i * 0.1}s both` }}
            >
              {link.label}
            </a>
          ))}
        </nav>
      )}

      {/* Social icons */}
      {socialLinks.length > 0 && (
        <div className="flex flex-wrap items-center justify-center gap-5 relative" style={{ animation: 'fadeIn 0.6s ease-out 1.2s both' }}>
          {socialLinks.map(({ icon: Icon, label, href }) => (
            <a
              key={label}
              href={href}
              aria-label={label}
              target={href.startsWith('http') ? '_blank' : undefined}
              rel={href.startsWith('http') ? 'noopener noreferrer' : undefined}
              className="rounded text-white/70 transition-colors duration-200 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50"
            >
              <Icon className="h-5 w-5" />
            </a>
          ))}
        </div>
      )}
    </div>
    </>
  )
}
